import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { LocalePathPipe } from '../../pipes/locale-path-pipe';
import { Logo } from '../../components/logo/logo';
import { Icon } from '../../components/icon/icon';
import { LegalDialog } from '../../components/legal-dialog/legal-dialog';
import { CtaLink } from '../../components/cta-link/cta-link';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { ConsentService } from '../../../core/services/consent.service';
import { ContentService } from '../../../core/services/content.service';
import { LanguageService } from '../../../core/services/language.service';
import { ProductContextService } from '../../../core/services/product-context.service';
import { DEMO_BOOKING_URL, ONBOARDING } from '../../../core/data/site.config';
import { isPlainClick } from '../../../core/utils/plain-click';
import { splitHref } from '../../../core/utils/split-href';

@Component({
  selector: 'av-footer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, TranslatePipe, LocalePathPipe, Logo, Icon, LegalDialog, CtaLink],
  templateUrl: './footer.html',
  styleUrl: './footer.scss',
})
export class Footer {
  private readonly content = inject(ContentService);
  private readonly context = inject(ProductContextService);
  private readonly language = inject(LanguageService);
  protected readonly consent = inject(ConsentService);
  private readonly analytics = inject(AnalyticsService);

  protected readonly site = this.content.site;

  protected readonly columns = computed(() => this.content.footer(this.context.slug()));
  /** The bar's own start and walkthrough, repeated where the page ends. */
  protected readonly actions = computed(() => this.content.headerActions(this.context.current()));
  protected readonly year = new Date().getFullYear();

  /**
   * The privacy policy is a static file under `public/`, one per language, so
   * that it reads with scripting off. It leaves the app, which is why this is
   * an href rather than a routerLink.
   */
  protected readonly privacyHref = computed(() => `/privacy-${this.language.lang()}.html`);

  protected readonly privacyOpen = signal(false);

  /**
   * Opens the document in a dialog, and otherwise gets out of the way: a
   * middle click, a modified click and a right-click "open in new tab" all
   * have to reach the file itself, and so does a visitor with no scripting.
   */
  protected openPrivacy(event: MouseEvent): void {
    if (!isPlainClick(event)) return;

    event.preventDefault();
    this.privacyOpen.set(true);
    this.analytics.privacyOpened();
  }

  /** The outbound links: a product's own application, Catalog's onboarding, or its demo booking. */
  protected reportOutboundLink(href: string): void {
    if (href === ONBOARDING.catalog) {
      this.analytics.ctaClicked({ id: 'footer_onboarding', location: 'footer', href });
      return;
    }
    if (href === DEMO_BOOKING_URL) {
      this.analytics.ctaClicked({ id: 'footer_demo_booking', location: 'footer', href });
      return;
    }
    this.analytics.signInClicked(href);
  }
  protected readonly splitHref = splitHref;
}
